import { useRef, useState } from 'react';
import { App, Alert, Button, Input, Modal, Segmented } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useImportCollection } from '@presentation/hooks/useCollections';
import type { ImportFormat } from '@core/repositories/CollectionRepository';

interface ImportCollectionModalProps {
  workspaceId: string;
  open: boolean;
  onClose: () => void;
}

const PLACEHOLDERS: Record<ImportFormat, string> = {
  postman: 'Paste a Postman Collection v2.1 JSON export here, or choose a file below…',
  curl: 'Paste a cURL command here, e.g.\ncurl -X POST https://api.example.com/login \\\n  -H "Content-Type: application/json" \\\n  -d \'{"email":"a@b.com"}\'',
  openapi:
    'Paste an OpenAPI 3.x / Swagger 2.0 document (JSON or YAML) here, or choose a file below…',
};

const ACCEPT: Partial<Record<ImportFormat, string>> = {
  postman: '.json,application/json',
  openapi: '.json,application/json,.yaml,.yml,text/yaml',
};

const FILE_HINT: Partial<Record<ImportFormat, string>> = {
  postman: 'Postman Collection v2.1 (.json)',
  openapi: 'OpenAPI/Swagger document (.json, .yaml, .yml)',
};

export const ImportCollectionModal = ({
  workspaceId,
  open,
  onClose,
}: ImportCollectionModalProps) => {
  const { message } = App.useApp();
  const [format, setFormat] = useState<ImportFormat>('postman');
  const [payload, setPayload] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importCollection = useImportCollection(workspaceId);

  const reset = () => {
    setPayload('');
    setFormat('postman');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const text = await file.text();
    setPayload(text);
  };

  const handleImport = () => {
    if (!payload.trim()) {
      void message.error(
        format === 'curl'
          ? 'Paste a cURL command first'
          : 'Paste or choose a collection file first',
      );
      return;
    }

    importCollection.mutate(
      { format, payload },
      {
        onSuccess: (collection) => {
          void message.success(`Imported "${collection.name}"`);
          handleClose();
        },
        onError: (error) => void message.error(error.message || 'Import failed'),
      },
    );
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title="Import"
      okText="Import"
      onOk={handleImport}
      confirmLoading={importCollection.isPending}
      destroyOnHidden
      width={640}
    >
      <div className="flex flex-col gap-3">
        <Segmented
          options={[
            { label: 'Postman Collection', value: 'postman' },
            { label: 'OpenAPI / Swagger', value: 'openapi' },
            { label: 'cURL command', value: 'curl' },
          ]}
          value={format}
          onChange={(value) => setFormat(value as ImportFormat)}
        />

        {ACCEPT[format] && (
          <div className="flex items-center gap-2">
            <Button
              size="small"
              icon={<UploadOutlined />}
              onClick={() => fileInputRef.current?.click()}
            >
              Choose file…
            </Button>
            <span className="text-xs text-gray-500">{FILE_HINT[format]}</span>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT[format]}
              hidden
              onChange={(event) => void handleFileChange(event)}
            />
          </div>
        )}

        <Input.TextArea
          value={payload}
          onChange={(event) => setPayload(event.target.value)}
          placeholder={PLACEHOLDERS[format]}
          autoSize={{ minRows: 10, maxRows: 16 }}
          className="font-mono text-xs"
        />

        <Alert
          type="info"
          showIcon
          title="What gets imported"
          description={
            format === 'postman'
              ? 'Folders, requests, headers, query/path params, body, pre-request/test scripts, and most auth types are imported. Auth types with no equivalent here (OAuth 1, NTLM, Hawk, EdgeGrid) fall back to "None" with a note added to the request description.'
              : format === 'openapi'
                ? 'Operations are grouped into folders by their first OpenAPI tag. Path/query/header parameters, bearer/basic/API key auth, and a JSON request body example generated from the schema are imported. Deeply nested or circular $ref schemas may not fully resolve.'
                : 'The URL, method, headers, -d/--data body, and -u basic auth are imported as a single request in a new collection.'
          }
        />
      </div>
    </Modal>
  );
};
